import {SliceState} from "../../../utils/Utils";
import {createAsyncThunk, createSelector, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {CriteriaAndPaging, fetchAll} from "./ControlListValueAPI";
import {RootState} from "../../../app/store";
import {ControlListValue, ControlListValueFetchSearchCriteria} from "./ControlListValue";
import {apiSlice} from "../../../app/apiSlice";
import {ControlListGroup} from "./ControlListGroup";
import {ControlListAction} from "./ControlListAction";
import {ControlListReason} from "./ControlListReason";
import {getStorageItem} from "../../../persistence";

export interface ControlListValueState {
    controlListValues?: ControlListValue[];
    status: SliceState;
    canGetMore: boolean;
    savedCriteria: ControlListValueFetchSearchCriteria;
    groupStatus: SliceState;
    groups: ControlListGroup[];
    actionStatus: SliceState;
    actions?: ControlListAction[];
    reasonsStatus: SliceState;
    reasons: ControlListReason[];
}


export const STORAGE_KEY_CONTROL_LIST_CRITERIA = 'control-list-search-criteria';

const initialState: ControlListValueState = {
    controlListValues: undefined,
    status: 'idle',
    canGetMore: true,
    savedCriteria: getStorageItem(STORAGE_KEY_CONTROL_LIST_CRITERIA, {}),
    groupStatus: 'idle',
    groups: [],
    actionStatus: 'idle',
    reasonsStatus: 'idle',
    reasons: []
};

export const fetchControlListValues = createAsyncThunk(
    'controlListValues/fetchAll',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

export const fetchMoreControlListValues = createAsyncThunk(
    'controlListValues/fetchMore',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

export const refetchAllControlListValues = createAsyncThunk(
    'controlListValues/refetch',
    async (query: CriteriaAndPaging) =>
        (await fetchAll(query)).data
);

function getDefaultControlListActionIds(actions: ControlListAction[]) {
    return actions
        .filter(a => a.name !== 'Whitelist')
        .map(a => a.id);
}

function getDefaultControlListReasonIds(payload: ControlListReason[]) {
    return payload
        .map(a => a.id);
}

export const controlListValueSlice = createSlice({
    name: 'controlListValue',
    initialState,
    reducers: {
        /**
         * This reducer action saves the provided search and paging criteria
         * into the Redux store's `controlListValues` slice. The `savedCriteria`
         * field is updated with the given payload, which represents the most
         * recent search criteria used for fetching control list values.
         *
         * @param RootState state - The root state
         * @param {ControlListValueFetchSearchCriteria} payload - The search and paging
         * criteria to be saved in the Redux store.
         */
        saveControlListValueCriteria: (state, {payload}: PayloadAction<ControlListValueFetchSearchCriteria>) => {
            state.savedCriteria = payload;
        },
        resetControlListValueCriteria: (state) => {
            state.savedCriteria = {
                controlListGroupId: state.groups[0].id,
                controlListActionIds: getDefaultControlListActionIds(state.groups),
                controlListReasonIds: getDefaultControlListReasonIds(state.reasons),
            }
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchControlListValues.pending, state => {
                state.status = 'loading'
            })
            .addCase(fetchControlListValues.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.controlListValues = action.payload;
                state.canGetMore = action.payload.length === 50;
            })
            .addCase(fetchControlListValues.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            })

            // fetch more
            .addCase(fetchMoreControlListValues.pending, state => {
                state.status = 'loading'
            })
            .addCase(fetchMoreControlListValues.fulfilled, (state, action) => {
                state.status = 'succeeded';
                if (action.payload.length) 
                    state.controlListValues = state.controlListValues?.concat(action.payload);
                state.canGetMore = action.payload.length === 50;
            })
            .addCase(fetchMoreControlListValues.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            })

            // refetch
            .addCase(refetchAllControlListValues.pending, state => {
                state.status = 'loading'
                state.controlListValues = [];
            })
            .addCase(refetchAllControlListValues.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.controlListValues = action.payload;
                state.canGetMore = action.payload.length === 50;
            })
            .addCase(refetchAllControlListValues.rejected, state => {
                state.status = 'failed';
                state.canGetMore = false;
            });
        
        builder
            .addMatcher(
                apiSlice.endpoints.getControlListGroups.matchPending,
                (state) => {
                    state.groupStatus = 'loading'
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListGroups.matchFulfilled,
                (state, {payload}) => {
                    state.groupStatus = 'succeeded';
                    state.groups = payload;
                    if (!state.savedCriteria.controlListGroupId) {
                        state.savedCriteria.controlListGroupId = payload[0].id;
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListGroups.matchRejected,
                (state) => {
                    state.groupStatus = 'failed'
                }
            );
        builder
            .addMatcher(
                apiSlice.endpoints.getControlListActions.matchPending,
                (state) => {
                    state.actionStatus = 'loading'
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListActions.matchFulfilled,
                (state, {payload}) => {
                    state.actionStatus = 'succeeded';
                    state.actions = payload;
                    if (!state.savedCriteria.controlListActionIds) {
                        state.savedCriteria.controlListActionIds = getDefaultControlListActionIds(payload);
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListActions.matchRejected,
                (state) => {
                    state.actionStatus = 'failed'
                }
            );

        builder
            .addMatcher(
                apiSlice.endpoints.getControlListReasons.matchPending,
                (state) => {
                    state.reasonsStatus = 'loading'
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListReasons.matchFulfilled,
                (state, {payload}) => {
                    state.reasonsStatus = 'succeeded';
                    state.reasons = payload;
                    if (!state.savedCriteria.controlListReasonIds) {
                        state.savedCriteria.controlListReasonIds = getDefaultControlListReasonIds(payload);
                    }
                }
            )
            .addMatcher(
                apiSlice.endpoints.getControlListReasons.matchRejected,
                (state) => {
                    state.reasonsStatus = 'failed'
                }
            );
    }
});

// Fetch hold flags
export const selectControlListValues = (state: RootState) => state.controlListValues.controlListValues;
export const canFetchMoreControlListValues = (state: RootState) => state.controlListValues.canGetMore;
export const isFetchingControlListValues = (state: RootState) => state.controlListValues.status === 'loading' ||
    state.controlListValues.status === 'idle';

/**
 * Selects the active group based  on the group id in the search criteria
 */
export const selectActiveControlListGroup = createSelector([
        (state: RootState) => state.controlListValues.groups,
        (state: RootState) => state.controlListValues.savedCriteria.controlListGroupId
    ],
    (groups, activeId) => groups.find(g => g.id === activeId)
);

/**
 * Select all control list groups that were returned from the query
 */
export const selectAllControlListGroups = createSelector([
        (state: RootState) => state.controlListValues.groups,
    ],
    groups => groups
);

/**
 * Select all control list actions that were returned from the query
 */
export const selectAllControlListActions = createSelector([
        (state: RootState) => state.controlListValues.actions
    ],
    actions => {
        return actions;
    }
);

/**
 * Selects all control list reasons that were returned from the query
 */
export const selectAllControlListReasons = createSelector([
        (state: RootState) => state.controlListValues.reasons,
    ],
    reasons => reasons
);

/**
 * Selector to retrieve the saved search criteria from the `controlListValues` state.
 *
 * This selector utilizes the `createSelector` utility from Reselect to memoize
 * the `savedCriteria` value of the `controlListValues` slice in the Redux store.
 * `savedCriteria` represents the most recently used search and paging criteria
 * for fetching control list values.
 *
 * @param {RootState} state - The root state of the Redux store.
 * @returns {ControlListValueFetchSearchCriteria} - The saved search criteria.
 *
 * Example usage:
 * ```ts
 * const savedCriteria = useSelector(selectSavedControlListCriteria);
 * ```
 */
export const selectSavedControlListCriteria = createSelector(
    [(s: RootState) => s.controlListValues.savedCriteria],
    (criteria) => criteria
);

const { reducer } = controlListValueSlice;

export const {
    saveControlListValueCriteria,
    resetControlListValueCriteria,
} = controlListValueSlice.actions;

export default reducer;